import { Collapse, Space } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Icon from '@ant-design/icons/lib/components/Icon';
import type {
  ReconDetailsBankRecord,
  ReconDetailsCardCostRecord,
  ReconDetailsCardRecord
} from '@melio/payments-recon-api-client-ts';
import { closeIcon } from '@tools/ui/assets';
import Loading from '@tools/ui/components/common/Loading';
import { theme } from '@tools/ui-utils/theme.util';
import { RECORD_PANEL_QUERY_PARAM_TRIGGER } from '../../../../consts/reco.const';
import { useApiRequest } from '../../../../hooks/useApiRequest';
import useQueryParams from '../../../../hooks/useQueryParams.hook';
import { BankRecordDetails } from './bank-record-details';
import { CardCostRecordDetails } from './card-cost-record-details';
import { CardRecordDetails } from './card-record-details';

const ANIMATION_OPTIONS: KeyframeAnimationOptions = {
  duration: 250,
  easing: 'ease-in-out',
  fill: 'forwards'
};

const Container = styled(Space)`
  position: fixed;
  width: 500px;
  top: 0;
  bottom: 0;
  right: 0;
  background: white;
  z-index: 100;
  padding: 30px;
  box-shadow: 0 9px 16px 8px rgba(0, 0, 0, 0.08), 0 6px 16px rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12);
  overflow: auto;
`;

const BackIconContainer = styled.div`
  font-size: 16px;
  width: 32px;
  height: 32px;
  display: grid;
  place-content: center;
  border-radius: 3px;

  &:hover {
    cursor: pointer;
    background: ${theme.colors.gray.grayLight};
  }
`;

export const RecordPanel: React.FC = () => {
  const panelRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const queryParams = useQueryParams();
  const [isVisible, setIsVisible] = useState(false);

  const ambiguousRecordId = useMemo(
    () => queryParams.get(RECORD_PANEL_QUERY_PARAM_TRIGGER),
    [queryParams]
  );

  const { error, loading, data, refetch } = useApiRequest<{
    bankRecords?: Array<ReconDetailsBankRecord>;
    cardRecords?: Array<ReconDetailsCardRecord>;
    cardCostRecords?: Array<ReconDetailsCardCostRecord>;
  }>({
    method: 'get',
    path: `/recon/find-records/${ambiguousRecordId}`,
    skip: !isVisible || !ambiguousRecordId
  });

  useEffect(() => {
    if (isVisible && !ambiguousRecordId) {
      panelRef.current
        ?.animate(
          [{ translate: '0 0' }, { translate: '100% 0' }],
          ANIMATION_OPTIONS
        )
        .finished.then(() => setIsVisible(false));
    }

    if (isVisible && ambiguousRecordId) {
      panelRef.current?.animate(
        [{ translate: '100% 0' }, { translate: '0 0' }],
        ANIMATION_OPTIONS
      );
    }

    if (!isVisible && ambiguousRecordId) {
      setIsVisible(true);
    }
  }, [isVisible, ambiguousRecordId]);

  const navigateBack = () => {
    queryParams.delete(RECORD_PANEL_QUERY_PARAM_TRIGGER);
    history.push({ search: queryParams.toString() });
  };

  if (!isVisible) {
    return null;
  }

  if (loading) {
    return (
      <Container direction="vertical" size={30} ref={panelRef}>
        <Space style={{ fontSize: 18 }} size={10}>
          <BackIconContainer onClick={navigateBack}>
            <Icon component={closeIcon} />
          </BackIconContainer>
        </Space>

        <Loading />
      </Container>
    );
  }

  if (!data) {
    return null;
  }

  const { cardRecords, bankRecords, cardCostRecords } = data;

  return (
    <Container direction="vertical" size={20} ref={panelRef}>
      <Space style={{ fontSize: 18 }} size={10}>
        <BackIconContainer onClick={navigateBack}>
          <Icon component={closeIcon} />
        </BackIconContainer>
      </Space>

      {bankRecords?.length === 1 && (
        <BankRecordDetails bankRecord={bankRecords[0]} refetch={refetch} />
      )}

      {!!bankRecords && bankRecords.length > 1 && (
        <Collapse
          size="small"
          items={bankRecords.map((bankRecord) => ({
            key: bankRecord.recordId!,
            label: bankRecord.recordId,
            children: (
              <BankRecordDetails bankRecord={bankRecord} refetch={refetch} />
            )
          }))}
        />
      )}

      {cardRecords?.length === 1 && (
        <CardRecordDetails cardRecord={cardRecords[0]} refetch={refetch} />
      )}

      {!!cardRecords && cardRecords.length > 1 && (
        <Collapse
          size="small"
          items={cardRecords.map((cardRecord) => ({
            key: cardRecord.recordId,
            label: cardRecord.recordId,
            children: (
              <CardRecordDetails cardRecord={cardRecord} refetch={refetch} />
            )
          }))}
        />
      )}

      {cardCostRecords?.length === 1 && (
        <CardCostRecordDetails cardCostRecord={cardCostRecords[0]} />
      )}

      {!!cardCostRecords && cardCostRecords.length > 1 && (
        <Collapse
          size="small"
          items={cardCostRecords.map((cardCostRecord) => ({
            key: cardCostRecord.recordId,
            label: cardCostRecord.recordId,
            children: (
              <CardCostRecordDetails cardCostRecord={cardCostRecord} />
            )
          }))}
        />
      )}
    </Container>
  );
};
